import { useEffect, useState } from "react";
import Select from "react-select";
import { useFetchData } from "../../hooks/useFetch";
import { ApiConfig, API_TRANSPORT_VEHICLE_ROUTER } from "../../services/api-config";

/**
 * Chọn PHƯƠNG TIỆN cho một chiều của đơn (Lấy hàng / Giao hàng): Xe máy | Xe tải.
 * Chọn xe tải = phân công CẢ ĐỘI HÌNH xe (tài xế + phụ xe, chia kg theo tỷ lệ) cho chiều đó — ô chọn tài xế của
 * chiều bị thay bằng đội hình xe. Xem user/docs/xe-tai-doi-hinh-ky-thuat.md §5.
 */
export interface VehicleOption {
  id: number;
  name: string;
  plate_number: string;
  area?: string | null;
  is_in_transit?: boolean;
  driver_id: number;
  driver_name: string;
  assistant_id: number | null;
  assistant_name: string | null;
  driver_ratio: number;
  assistant_ratio: number;
}

interface Props {
  label: string;
  /** null = xe máy */
  value: number | null;
  onChange: (vehicleId: number | null, vehicle?: VehicleOption) => void;
  /** Chiều đã bắt đầu (đã có người nhận) → chỉ hiển thị, không đổi được (BE cũng chặn). */
  disabled?: boolean;
  disabledHint?: string;
}

/** "Tài xế A (60%) · Phụ xe B (40%)" */
export const crewText = (v: Pick<VehicleOption, "driver_name" | "assistant_name" | "driver_ratio" | "assistant_ratio">) =>
  v.assistant_name
    ? `Tài xế ${v.driver_name} (${Number(v.driver_ratio)}%) · Phụ xe ${v.assistant_name} (${Number(v.assistant_ratio)}%)`
    : `Tài xế ${v.driver_name} (100%)`;

const VehicleLegPicker = ({ label, value, onChange, disabled, disabledHint }: Props) => {
  const { request } = useFetchData.Get<VehicleOption[]>({ projectId: ApiConfig.PROJECT_ID.ORDER_SERVICE });
  const [vehicles, setVehicles] = useState<VehicleOption[]>([]);
  const [useTruck, setUseTruck] = useState<boolean>(!!value);

  useEffect(() => setUseTruck(!!value), [value]);

  useEffect(() => {
    let cancelled = false;
    request({ endPoint: API_TRANSPORT_VEHICLE_ROUTER.API_VEHICLE_OPTIONS }).then((res: any) => {
      if (cancelled) return;
      const list = res?.data ?? [];
      setVehicles(Array.isArray(list) ? list : []);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const options = vehicles.map((v) => ({
    value: v.id,
    label: `${v.name} · ${v.plate_number}${v.is_in_transit ? " (đang chạy chuyến)" : ""}`,
    raw: v,
  }));
  const selected = options.find((o) => o.value === value) || null;
  // Xe đang chọn nhưng không còn trong danh sách (tạm ngưng / bỏ tài xế) — vẫn hiện để không "mất" giá trị.
  const selectedFallback = !selected && value ? { value, label: `Xe #${value} (không còn dùng được)`, raw: null } : null;

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-3 text-xs">
        <span className="text-slate-500">{label}:</span>
        <label className="inline-flex items-center gap-1 cursor-pointer">
          <input
            type="radio"
            disabled={disabled}
            checked={!useTruck}
            onChange={() => {
              setUseTruck(false);
              onChange(null);
            }}
          />
          Xe máy
        </label>
        <label className="inline-flex items-center gap-1 cursor-pointer">
          <input type="radio" disabled={disabled} checked={useTruck} onChange={() => setUseTruck(true)} />
          Xe tải
        </label>
      </div>
      {useTruck && (
        <>
          <Select
            isDisabled={disabled}
            placeholder="Chọn xe tải..."
            options={options}
            value={selected || selectedFallback}
            onChange={(o: any) => onChange(o ? o.value : null, o?.raw || undefined)}
            menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
            menuPosition="fixed"
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
            noOptionsMessage={() => "Không có xe tải nào (xe phải Hoạt động và có tài xế)"}
          />
          {selected?.raw && <p className="text-xs text-green-700">{crewText(selected.raw)}</p>}
        </>
      )}
      {disabled && disabledHint && <p className="text-xs text-slate-400">{disabledHint}</p>}
    </div>
  );
};

export default VehicleLegPicker;
